import { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
	DetailSummary,
	DetailTabs,
} from "@/controls/detail-panel/DetailPanel";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import { sanitizeNullStrings } from "@/utils/sanitize";
import type { ProductSize } from "../models/ProductSize";
import { ProductTypeSelector } from "./ProductTypeSelector";
import { ProductMillSetupTable } from "./ProductMillSetupTable";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";

interface FormErrors {
	Name?: string;
}

interface ProductDetailViewerProps {
	product: ProductSize | null;
	existingProducts: ProductSize[];
	isSaving: boolean;
	onSave: (product: ProductSize) => void;
	onDuplicate: (product: ProductSize) => void;
	onClose: () => void;
}

const TABS = ["mill-setup", "info"] as const;
type ProductTab = (typeof TABS)[number];

const URL_SCHEMA = {
	tab: urlParam.oneOf(TABS, "mill-setup"),
};

const formatNumber = (value: number | null, decimals: number) =>
	value === null || value === undefined ? null : value.toFixed(decimals);

export const ProductDetailViewer = ({
	product,
	existingProducts,
	isSaving,
	onSave,
	onDuplicate,
	onClose,
}: ProductDetailViewerProps) => {
	const [formData, setFormData] = useState<ProductSize | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewProduct = !product?.Id;
	const [{ tab }, setUrlState] = useUrlState(URL_SCHEMA);
	const activeTab: ProductTab = isNewProduct ? "info" : tab;
	const [isAddingSetup, setIsAddingSetup] = useState(false);

	const validateForm = useMemo(() => {
		return (data: ProductSize | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingProducts.some(
					(p) =>
						p.Id !== data.Id &&
						p.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingProducts]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewProduct && isFormValid;

	useEffect(() => {
		if (product) {
			setFormData(sanitizeNullStrings({ ...product }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [product]);

	const handleChange = (field: keyof ProductSize, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof ProductSize) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof ProductSize, value: unknown) => {
		handleBlur(field);

		if (isNewProduct) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = product?.[field];
		if (originalValue !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleSelectChange = (field: keyof ProductSize, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewProduct) return;

		if (product?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (
		field: keyof ProductSize,
		rawValue: string,
	) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!product) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a product to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewProduct ? "New Product" : formData.Name}
				active={isNewProduct ? undefined : product.Active}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNewProduct ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : (
						<Button
							variant="contained"
							color="success"
							size="small"
							onClick={() => onDuplicate(formData)}
							disabled={isSaving}
						>
							DUPLICATE
						</Button>
					)
				}
			/>

			<DetailBody>
				{!isNewProduct && (
					<DetailSummary
						items={[
							{
								label: "Product Type",
								value: product.ProductTypeName,
								grow: 1.2,
							},
							{ label: "Section Code", value: product.SectionCode },
							{
								label: "Weight/Ft",
								value: formatNumber(product.Weight, 3),
								grow: 0.7,
							},
							{
								label: "Finish Speed",
								value: formatNumber(product.FinishSpeedGoal, 2),
								grow: 0.7,
							},
							{
								label: "TPH Goal",
								value: formatNumber(product.TPHGoal, 2),
								grow: 0.7,
							},
							{
								grow: 1.9,
								label: "Revision",
								value: product.RevisionTimestamp
									? `${dxtToLocalServerTime(`${product.RevisionTimestamp}`, "MM/dd/yyyy")} by ${product.RevisionUserName ?? "-"}`
									: null,
							},
						]}
					/>
				)}

				<DetailTabs<ProductTab>
					value={activeTab}
					onChange={(next) => setUrlState({ tab: next })}
					flush={activeTab === "mill-setup"}
					actions={
						activeTab === "mill-setup" && (
							<Button
								variant="contained"
								color="primary"
								size="small"
								startIcon={<AddIcon />}
								onClick={() => setIsAddingSetup(true)}
								disabled={isSaving}
							>
								ADD STAND
							</Button>
						)
					}
					tabs={[
						{
							value: "info",
							label: "Product Info",
						},
						{
							value: "mill-setup",
							label: "Mill Setup",
							disabled: isNewProduct,
						},
					]}
				>
					{activeTab === "mill-setup" && formData.Id ? (
						<ProductMillSetupTable
							productSizeId={formData.Id}
							disabled={isSaving}
							isAdding={isAddingSetup}
							onAddDone={() => setIsAddingSetup(false)}
						/>
					) : (
						<>
							<DetailSection
								title="Basic Information"
								icon={<InfoOutlinedIcon />}
							>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Name"
											value={formData.Name}
											onChange={(e) => handleChange("Name", e.target.value)}
											onBlur={(e) => handleBlurSave("Name", e.target.value)}
											fullWidth
											size="small"
											required
											error={touched.Name && !!errors.Name}
											helperText={touched.Name && errors.Name}
											slotProps={{ inputLabel: { shrink: true } }}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<ProductTypeSelector
											value={formData.ProductTypeId}
											onChange={(value) =>
												handleSelectChange("ProductTypeId", value)
											}
											fullWidth
											size="small"
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Section Code"
											value={formData.SectionCode ?? ""}
											onChange={(e) =>
												handleChange("SectionCode", e.target.value)
											}
											onBlur={(e) =>
												handleBlurSave("SectionCode", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{ inputLabel: { shrink: true } }}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Description"
											value={formData.Description ?? ""}
											onChange={(e) =>
												handleChange("Description", e.target.value)
											}
											onBlur={(e) =>
												handleBlurSave("Description", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{ inputLabel: { shrink: true } }}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12 }}>
										<FormControlLabel
											control={
												<Checkbox
													checked={!!formData.Active}
													onChange={(e) =>
														handleSelectChange("Active", e.target.checked)
													}
													size="small"
												/>
											}
											label="Active"
											sx={{ ml: 0 }}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>

							<DetailSection
								title="Production Goals"
								icon={<FlagOutlinedIcon />}
							>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Finish Speed Goal"
											type="number"
											value={formData.FinishSpeedGoal ?? ""}
											onChange={(e) =>
												handleChange(
													"FinishSpeedGoal",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("FinishSpeedGoal", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Stand 1 Gap Goal"
											type="number"
											value={formData.Stand1GapGoal ?? ""}
											onChange={(e) =>
												handleChange(
													"Stand1GapGoal",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("Stand1GapGoal", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Stand 17 Gap Goal"
											type="number"
											value={formData.Stand17GapGoal ?? ""}
											onChange={(e) =>
												handleChange(
													"Stand17GapGoal",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("Stand17GapGoal", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="TPH Goal"
											type="number"
											value={formData.TPHGoal ?? ""}
											onChange={(e) =>
												handleChange(
													"TPHGoal",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("TPHGoal", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>

							<DetailSection
								title="Physical Properties"
								icon={<StraightenOutlinedIcon />}
							>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Weight"
											type="number"
											value={formData.Weight ?? ""}
											onChange={(e) =>
												handleChange(
													"Weight",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("Weight", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Dim 1"
											type="number"
											value={formData.Dim1 ?? ""}
											onChange={(e) =>
												handleChange(
													"Dim1",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("Dim1", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Dim 2"
											type="number"
											value={formData.Dim2 ?? ""}
											onChange={(e) =>
												handleChange(
													"Dim2",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("Dim2", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<TextField
											label="Dim 3"
											type="number"
											value={formData.Dim3 ?? ""}
											onChange={(e) =>
												handleChange(
													"Dim3",
													e.target.value === ""
														? null
														: Number.parseFloat(e.target.value),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("Dim3", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 0.01 },
											}}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>

							<DetailSection
								title="Mill Comments"
								icon={<ChatBubbleOutlineIcon />}
							>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12 }}>
										<TextField
											label="Special Mill Comments"
											value={formData.SpecialMillComments ?? ""}
											onChange={(e) =>
												handleChange("SpecialMillComments", e.target.value)
											}
											onBlur={(e) =>
												handleBlurSave("SpecialMillComments", e.target.value)
											}
											fullWidth
											size="small"
											multiline
											rows={3}
											slotProps={{ inputLabel: { shrink: true } }}
											sx={EDITABLE_FIELD_SX}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>
						</>
					)}
				</DetailTabs>
			</DetailBody>
		</Box>
	);
};
